import React, { useCallback, useMemo, useState } from "react";
import Lightbox from "./Lightbox";
import { Box, Grid } from "@mui/material";
import {
    GridItem,
    OriginalAspectRatioImageContainer,
    SquareAspectRatioImageContainer,
} from "./ImageGrid.styles";
import "./ImageGrid.styles.css";
import { useLockBodyScroll } from "../../../Infrastructure/Hooks/useLockBodyScroll";
import { addPxPostfix } from "../../Shared/addPxPostfix";

export enum IMAGE_GRID_TYPES {
    DEFAULT = "default",
    SMALL = "small",
}

export const MESSAGE_IMAGE_MAX_WIDTH = 240;
export const MESSAGE_IMAGE_MAX_WIDTH_SMALL_SCREEN = 160;
export const MESSAGE_SCREEN_WIDTH_BREAK_POINT = 640;

const MAXIMUM_NUMBER_OF_SHOWN_IMAGES = 5;

export type MessageContentImage = {
    height?: number;
    id?: number;
    url?: string;
    width?: number;
};

const isSmallScreen = () =>
    window.innerWidth <= MESSAGE_SCREEN_WIDTH_BREAK_POINT;

function calculateImageBlockHeight(image?: MessageContentImage) {
    if (!image || image.width === undefined || image.height === undefined) {
        return 0;
    }

    const imageMaxWidth = isSmallScreen()
        ? MESSAGE_IMAGE_MAX_WIDTH_SMALL_SCREEN
        : MESSAGE_IMAGE_MAX_WIDTH;

    if (image.width < imageMaxWidth) {
        return image.height;
    }

    return (imageMaxWidth / image.width) * image.height;
}

export type ImageType = {
    url: string;
    height?: number;
    width?: number;
};

const Images = ({
    images = [] as ImageType[],
    onClickEach = undefined,
    numberOfShownImages = 5,
    imageGridType = IMAGE_GRID_TYPES.DEFAULT,
}: {
    images: ImageType[];
    imageGridType?: IMAGE_GRID_TYPES;
    numberOfShownImages?: number;
    onClickEach?: ({ src, index }: { src: string; index: number }) => void;
}) => {
    const [showLightbox, setShowLightbox] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    const actualNumberOfShownImages = useMemo(() => {
        if (
            numberOfShownImages <= 0 ||
            numberOfShownImages > MAXIMUM_NUMBER_OF_SHOWN_IMAGES
        ) {
            return MAXIMUM_NUMBER_OF_SHOWN_IMAGES;
        }
        return Math.min(numberOfShownImages, images.length);
    }, [numberOfShownImages, images]);

    const openLightbox = useCallback(
        (index: number) => {
            if (onClickEach) {
                return onClickEach({
                    src: images[index].url,
                    index,
                });
            }
            setLightboxIndex(index);
            setShowLightbox(true);
        },
        [onClickEach, images.length],
    );

    const closeLightbox = useCallback(() => {
        setShowLightbox(false);
    }, []);

    useLockBodyScroll(showLightbox);

    const renderCountOverlay = useCallback(
        (more: boolean) => {
            const extra = images.length - actualNumberOfShownImages;
            return [
                more && <div key="count" className="cover"></div>,
                more && (
                    <div
                        key="count-sub"
                        className="cover-text"
                        style={{ fontSize: "200%" }}
                    >
                        <p>+{extra + 1}</p>
                    </div>
                ),
            ];
        },
        [actualNumberOfShownImages, images.length],
    );

    const imageHeight = addPxPostfix(calculateImageBlockHeight(images[0]));
    const renderOneImageUsingOriginalAspectRatio = (index?: number) => {
        return (
            <Grid container columnSpacing={"4px"} mb={"4px"}>
                <GridItem item xs={12} onClick={() => openLightbox(index ?? 0)}>
                    <OriginalAspectRatioImageContainer
                        imageHeight={imageHeight}
                    >
                        {index !== undefined && <img src={images[index].url} />}
                        {index === undefined && <img src={images[0].url} />}
                    </OriginalAspectRatioImageContainer>
                </GridItem>
            </Grid>
        );
    };

    const renderTwoImagesUsingSquareFormat = (
        firstIndex: number,
        secondIndex: number,
    ) => {
        return (
            <Grid container columnSpacing={"4px"} mb={"4px"}>
                <GridItem item xs={6} onClick={() => openLightbox(firstIndex)}>
                    <SquareAspectRatioImageContainer>
                        <img src={images[firstIndex].url} />
                    </SquareAspectRatioImageContainer>
                </GridItem>
                <GridItem item xs={6} onClick={() => openLightbox(secondIndex)}>
                    <SquareAspectRatioImageContainer>
                        <img src={images[secondIndex].url} />
                    </SquareAspectRatioImageContainer>
                </GridItem>
            </Grid>
        );
    };

    const renderThreeImagesUsingSquareFormat = (
        firstIndex: number,
        secondIndex: number,
        thirdIndex: number,
    ) => {
        const countOverlay =
            images.length > actualNumberOfShownImages
                ? renderCountOverlay(true)
                : undefined;

        return (
            <Grid container columnSpacing={"4px"}>
                <GridItem item xs={4} onClick={() => openLightbox(firstIndex)}>
                    <SquareAspectRatioImageContainer>
                        <img src={images[firstIndex].url} />
                    </SquareAspectRatioImageContainer>
                </GridItem>
                <GridItem item xs={4} onClick={() => openLightbox(secondIndex)}>
                    <SquareAspectRatioImageContainer>
                        <img src={images[secondIndex].url} />
                    </SquareAspectRatioImageContainer>
                </GridItem>
                <GridItem item xs={4} onClick={() => openLightbox(thirdIndex)}>
                    <SquareAspectRatioImageContainer>
                        <img src={images[thirdIndex].url} />
                    </SquareAspectRatioImageContainer>
                    {countOverlay}
                </GridItem>
            </Grid>
        );
    };

    return (
        <Box>
            {[1, 3, 4].includes(actualNumberOfShownImages) &&
                renderOneImageUsingOriginalAspectRatio()}
            {[2, 5].includes(actualNumberOfShownImages) &&
                renderTwoImagesUsingSquareFormat(0, 1)}
            {[3].includes(actualNumberOfShownImages) &&
                renderTwoImagesUsingSquareFormat(1, 2)}
            {[4].includes(actualNumberOfShownImages) &&
                renderThreeImagesUsingSquareFormat(1, 2, 3)}
            {[5].includes(actualNumberOfShownImages) &&
                renderThreeImagesUsingSquareFormat(2, 3, 4)}

            {showLightbox && (
                <Lightbox
                    onClose={closeLightbox}
                    initialIndex={lightboxIndex}
                    images={images.map((image) => image.url)}
                    showNextPrevButtons={images.length > 1}
                />
            )}
        </Box>
    );
};

export default Images;
